import { useCallback, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Check, ImageIcon, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";

/**
 * BulkPhotoUploader
 * ---
 * Drag many files onto the modal. Each file is auto-matched to a product by
 * filename (base slug match, e.g. `bamboo-border-duvet-1.jpg` → the closest
 * product slug). Admin can review, adjust, or skip per row before committing.
 */
export const BulkPhotoUploader = ({ open, onClose, products, onUpload }) => {
  const [rows, setRows] = useState([]); // {file, previewUrl, slug, status, error}
  const [busy, setBusy] = useState(false);
  const dropRef = useRef(null);

  const activeProducts = useMemo(
    () => (products || []).filter((p) => !p.is_deleted),
    [products]
  );

  const slugFromFilename = useCallback(
    (name) => {
      const stem = name.replace(/\.[^.]+$/, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      // 1. Exact slug match
      const exact = activeProducts.find((p) => stem === p.slug);
      if (exact) return exact.slug;
      // 2. Prefix / contains
      const contains = activeProducts.find((p) => stem.includes(p.slug) || p.slug.includes(stem));
      if (contains) return contains.slug;
      // 3. Fuzzy: longest common token overlap
      const stemTokens = new Set(stem.split("-").filter(Boolean));
      let best = null, bestScore = 0;
      for (const p of activeProducts) {
        const pTokens = p.slug.split("-");
        const overlap = pTokens.filter((t) => stemTokens.has(t)).length;
        if (overlap > bestScore) { best = p.slug; bestScore = overlap; }
      }
      return bestScore >= 2 ? best : "";
    },
    [activeProducts]
  );

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    const next = arr.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      slug: slugFromFilename(file.name),
      status: "pending",
      error: "",
      id: `${file.name}-${file.size}-${file.lastModified}`,
    }));
    setRows((r) => {
      const seen = new Set(r.map((x) => x.id));
      return [...r, ...next.filter((x) => !seen.has(x.id))];
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove("ring-forest");
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add("ring-forest");
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("ring-forest");
  };

  const changeSlug = (id, slug) => setRows((r) => r.map((x) => (x.id === id ? { ...x, slug } : x)));
  const removeRow = (id) => setRows((r) => r.filter((x) => x.id !== id));

  const commit = async () => {
    const toUpload = rows.filter((r) => r.slug && r.status !== "done");
    if (!toUpload.length) return toast.error("Match at least one file to a product first");
    setBusy(true);
    for (const row of toUpload) {
      setRows((r) => r.map((x) => (x.id === row.id ? { ...x, status: "uploading", error: "" } : x)));
      try {
        await onUpload(row.slug, row.file);
        setRows((r) => r.map((x) => (x.id === row.id ? { ...x, status: "done" } : x)));
      } catch (e) {
        setRows((r) => r.map((x) => (x.id === row.id ? { ...x, status: "error", error: e?.message || "Failed" } : x)));
      }
    }
    setBusy(false);
    const okCount = rows.filter((r) => r.status === "done").length + toUpload.filter((r) => r.slug).length;
    toast.success(`Uploaded ${okCount} studio photos`);
  };

  const clearAll = () => {
    rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
    setRows([]);
  };

  const matched = rows.filter((r) => r.slug).length;
  const unmatched = rows.length - matched;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => (!busy ? onClose() : null)}
          data-testid="bulk-uploader"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-hairline"
          >
            <header className="sticky top-0 bg-white border-b border-hairline px-6 md:px-8 py-5 flex items-center justify-between">
              <div>
                <div className="overline text-champagne">§ Bulk photo upload</div>
                <h2 className="font-display text-forest text-2xl md:text-3xl leading-none mt-1">
                  Drop many <span className="font-editorial italic text-champagne">studio shots</span>.
                </h2>
              </div>
              <button
                onClick={onClose} disabled={busy}
                data-testid="close-bulk"
                className="p-2 -mr-2 text-forest/60 hover:text-forest disabled:opacity-40"
                aria-label="Close"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            <div className="p-6 md:p-8">
              <div
                ref={dropRef}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className="border-2 border-dashed border-hairline rounded-lg bg-cream p-10 text-center transition-colors ring-0 hover:ring-2 hover:ring-champagne/40"
                data-testid="dropzone"
              >
                <UploadCloud size={32} strokeWidth={1.4} className="text-champagne mx-auto mb-3" />
                <p className="font-display text-forest text-2xl leading-tight">
                  Drag photos here, or <label htmlFor="bulk-file-input" className="font-editorial italic text-champagne underline decoration-champagne/30 cursor-pointer">choose files</label>
                </p>
                <p className="text-forest/60 text-sm mt-2">
                  Name each file after your product slug (e.g. <span className="font-mono">bamboo-border-duvet.jpg</span>) — we auto-match.
                </p>
                <input
                  id="bulk-file-input"
                  data-testid="bulk-file-input"
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
              </div>

              {rows.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-forest/70">
                      <span data-testid="bulk-matched" className="text-forest">{matched}</span> matched · <span className="text-forest">{unmatched}</span> to review · {rows.length} total
                    </div>
                    <button onClick={clearAll} disabled={busy} className="overline text-forest/60 hover:text-forest disabled:opacity-40">
                      Clear all
                    </button>
                  </div>

                  <div className="border border-hairline divide-y divide-[hsl(var(--beige-2))] max-h-[50vh] overflow-y-auto">
                    {rows.map((row) => (
                      <div key={row.id} className="p-3 flex items-center gap-3">
                        <div className="w-16 h-20 bg-bone border border-hairline overflow-hidden shrink-0">
                          <img src={row.previewUrl} alt="" className="h-full w-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-forest text-sm truncate" title={row.file.name}>{row.file.name}</div>
                          <div className="text-forest/50 text-xs">{(row.file.size / 1024).toFixed(0)} KB</div>
                        </div>
                        <select
                          data-testid={`bulk-slug-${row.id}`}
                          value={row.slug}
                          disabled={busy || row.status === "done"}
                          onChange={(e) => changeSlug(row.id, e.target.value)}
                          className="bg-transparent border border-hairline rounded-full px-3 py-1.5 text-sm text-forest focus:border-forest focus:outline-none max-w-[240px]"
                        >
                          <option value="">— pick a product —</option>
                          {activeProducts.map((p) => (
                            <option key={p.slug} value={p.slug}>{p.title}</option>
                          ))}
                        </select>
                        <div className="w-24 text-right">
                          {row.status === "done" && (
                            <span className="inline-flex items-center gap-1 overline text-[10px] bg-forest text-white rounded-full px-2 py-1">
                              <Check size={11} strokeWidth={1.8} /> Uploaded
                            </span>
                          )}
                          {row.status === "uploading" && (
                            <span className="overline text-[10px] text-forest/60">Uploading…</span>
                          )}
                          {row.status === "error" && (
                            <span className="overline text-[10px] text-[hsl(0_60%_45%)]" title={row.error}>Failed</span>
                          )}
                          {row.status === "pending" && !row.slug && (
                            <span className="overline text-[10px] text-forest/40">Waiting</span>
                          )}
                        </div>
                        {row.status !== "uploading" && (
                          <button
                            onClick={() => removeRow(row.id)}
                            disabled={busy}
                            className="text-forest/40 hover:text-forest p-1"
                            aria-label="Remove"
                          >
                            <Trash2 size={14} strokeWidth={1.6} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <footer className="sticky bottom-0 bg-white border-t border-hairline px-6 md:px-8 py-5 flex items-center justify-between gap-3">
              <p className="text-forest/50 text-xs">Only matched rows will upload. Max 8 MB per photo.</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="overline text-forest border border-hairline rounded-full px-4 py-2 hover:border-forest disabled:opacity-40"
                >
                  {busy ? "Uploading…" : "Close"}
                </button>
                <button
                  type="button"
                  data-testid="bulk-commit"
                  onClick={commit}
                  disabled={busy || matched === 0}
                  className="inline-flex items-center gap-2 overline text-white bg-forest rounded-full px-5 py-2.5 disabled:opacity-40"
                >
                  <Upload size={13} strokeWidth={1.8} /> {busy ? "Uploading…" : `Upload ${matched} photos`}
                </button>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
